import { motion, useReducedMotion } from 'framer-motion';
import { MapPin, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { type Country, type Rating, ratingLabels } from '@/data/countries';
import { useCountryDisplayData } from '@/hooks/useCountryDisplayData';
import { useGlobalStats } from '@/hooks/useSync';
import { cn } from '@/lib/utils';
import { CountryDetails } from './CountryDetails';
import { CountryLocator } from './CountryLocator';
import { CountryImageCarousel } from './tournament/CountryImageCarousel';

interface CountryCardProps {
  country: Country;
  rating: number | null;
  onRate: (rating: number) => void;
}

export function CountryCard({ country, rating, onRate }: CountryCardProps) {
  const [imageExpanded, setImageExpanded] = useState(false);
  const [showScrollFade, setShowScrollFade] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  // Check if content is scrollable and update fade visibility
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    setShowScrollFade(hasOverflow && !isAtBottom);
  }, []);

  // Use shared hook for display data
  const displayData = useCountryDisplayData(country.code);

  // Check initial scroll state after content loads
  useEffect(() => {
    // Small delay to ensure content is rendered
    const timer = setTimeout(handleScroll, 100);
    return () => clearTimeout(timer);
  }, [handleScroll, displayData]);
  const { images } = displayData;

  // Fetch global stats for this country
  const countryCodes = useMemo(() => [country.code], [country.code]);
  const { stats } = useGlobalStats(countryCodes);
  const globalStats = stats[country.code];

  return (
    <Card
      noPadding
      className={cn(
        'h-full bg-card border border-border/60 flex flex-col',
        !imageExpanded && 'overflow-hidden',
      )}
    >
      {/* Top: Map + Image side by side */}
      <div
        className={cn(
          'shrink-0 flex h-[140px] sm:h-[160px] md:h-[180px] lg:h-[200px]',
          !imageExpanded && 'overflow-hidden',
        )}
      >
        {/* Map - left side */}
        <div className="w-[35%] min-w-[120px] border-r border-border/40 bg-sky-50/30 overflow-hidden">
          <CountryLocator
            countryName={country.name}
            countryCode={country.code}
          />
        </div>

        {/* Image carousel - right side */}
        <div className={cn('flex-1', !imageExpanded && 'overflow-hidden')}>
          {images.length > 0 ? (
            <CountryImageCarousel
              images={images}
              countryName={country.name}
              fillContainer
              onExpandedChange={setImageExpanded}
            />
          ) : (
            <div className="w-full h-full bg-linear-to-br from-muted/30 to-muted/50 flex items-center justify-center">
              <span className="text-6xl opacity-30">{country.flag}</span>
            </div>
          )}
        </div>
      </div>

      {/* Middle: Header + Scrollable Info */}
      <div className="flex-1 min-h-0 flex flex-col px-4 py-3 lg:px-5 lg:py-4">
        {/* Header with flag and name - fixed */}
        <div className="shrink-0 flex items-center gap-3 pb-3 border-b border-border/40">
          <motion.div
            className="text-3xl lg:text-4xl filter drop-shadow-md shrink-0"
            whileHover={
              shouldReduceMotion ? undefined : { scale: 1.1, rotate: 5 }
            }
            transition={
              shouldReduceMotion
                ? { duration: 0 }
                : { type: 'spring', stiffness: 400 }
            }
          >
            {country.flag}
          </motion.div>
          <div className="flex-1 min-w-0 flex gap-2 justify-between">
            <h2 className="font-display text-xl lg:text-2xl font-medium leading-tight tracking-tight">
              {country.name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
                <MapPin className="w-2.5 h-2.5" />
                {country.subregion || country.continent}
              </span>
            </div>
          </div>
        </div>

        {/* Scrollable details section */}
        <div className="relative flex-1 min-h-0">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto py-3 -mx-1 px-1"
          >
            <CountryDetails data={displayData} compact />
          </div>
          {/* Scroll fade indicator */}
          <div
            className={cn(
              'pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent transition-opacity duration-200',
              showScrollFade ? 'opacity-100' : 'opacity-0'
            )}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Bottom: Rating buttons - always visible */}
      <div className="shrink-0 px-4 pb-4 lg:px-5 lg:pb-5 pt-2 border-t border-border/40 bg-muted/20">
        {/* Rating buttons row */}
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((value) => {
            const isSelected = rating === value;
            const colors = getRatingButtonColors(value, isSelected);
            const label = ratingLabels[value as Rating].label;

            return (
              <motion.button
                key={value}
                onClick={() => onRate(value)}
                whileHover={shouldReduceMotion ? undefined : { scale: 1.02 }}
                whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
                aria-label={`Rate ${country.name} as ${label} (${value} out of 5)`}
                aria-pressed={isSelected}
                className={`
                  flex-1 py-3 lg:py-4 rounded-lg
                  flex flex-col items-center justify-center gap-0.5
                  transition-all duration-150 border-2
                  ${colors}
                `}
              >
                <span className="font-display text-lg lg:text-xl font-bold leading-none">
                  {value}
                </span>
                <span
                  className={`hidden md:block text-[9px] lg:text-[10px] font-semibold uppercase tracking-wide leading-none ${isSelected ? 'text-white/90' : 'opacity-60'}`}
                >
                  {label}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* Global stats - compact */}
        {globalStats && globalStats.totalVotes > 0 && (
          <div className="flex items-center justify-center gap-1.5 mt-2.5 text-[11px] text-muted-foreground">
            <Users className="w-3 h-3" />
            <span className="font-medium text-foreground tabular-nums">
              {globalStats.avgRating.toFixed(1)}
            </span>
            <span>avg from</span>
            <span className="font-medium text-foreground tabular-nums">
              {globalStats.totalVotes}
            </span>
            <span>{globalStats.totalVotes === 1 ? 'vote' : 'votes'}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Get button color classes based on rating value and selection state */
function getRatingButtonColors(value: number, isSelected: boolean): string {
  const colorMap: Record<number, { base: string; selected: string }> = {
    1: {
      base: 'bg-red-50 hover:bg-red-100 border-red-200/70 text-red-700',
      selected:
        'bg-red-600 border-red-700 text-white shadow-md ring-2 ring-red-500/20',
    },
    2: {
      base: 'bg-orange-50 hover:bg-orange-100 border-orange-200/70 text-orange-700',
      selected:
        'bg-orange-500 border-orange-600 text-white shadow-md ring-2 ring-orange-500/20',
    },
    3: {
      base: 'bg-amber-50 hover:bg-amber-100 border-amber-200/70 text-amber-700',
      selected:
        'bg-amber-500 border-amber-600 text-white shadow-md ring-2 ring-amber-500/20',
    },
    4: {
      base: 'bg-lime-50 hover:bg-lime-100 border-lime-200/70 text-lime-700',
      selected:
        'bg-lime-600 border-lime-700 text-white shadow-md ring-2 ring-lime-500/20',
    },
    5: {
      base: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200/70 text-emerald-700',
      selected:
        'bg-emerald-600 border-emerald-700 text-white shadow-md ring-2 ring-emerald-500/20',
    },
  };

  const colors = colorMap[value] || colorMap[3];
  return isSelected ? colors.selected : colors.base;
}
